import { MAX_FILESTACK_FILE_SIZE } from '@aline/common';
import { acceptedMimeTypePrefixes, acceptedMimeTypes } from 'src/helpers/integrations/filestack';

import { MAX_REPOSITORY_UPLOAD_FILES } from '../../../RepositoryDropzoneConfig';

interface ValidateDroppedFilesResult {
  invalidMimeCount: number;
  /** Unique MIME types of the skipped files, in drop order; empty types report as 'unknown file type'. */
  invalidMimeTypes: string[];
  invalidSizeCount: number;
  tooMany: boolean;
  validFiles: File[];
}

/**
 * Validates dropped files by MIME type and size.
 * Returns valid files plus per-reason skip counts so callers can notify precisely.
 * A file that is both oversized and an unsupported type counts once, as oversized.
 */
export function validateDroppedFiles(files: File[]): ValidateDroppedFilesResult {
  if (files.length > MAX_REPOSITORY_UPLOAD_FILES) {
    return {
      invalidMimeCount: 0,
      invalidMimeTypes: [],
      invalidSizeCount: 0,
      tooMany: true,
      validFiles: [],
    };
  }

  const sizedFiles = files.filter(getIsAcceptedSize);
  const validFiles = sizedFiles.filter(getIsAcceptedMimeType);
  const invalidMimeFiles = sizedFiles.filter(file => !getIsAcceptedMimeType(file));

  return {
    invalidMimeCount: invalidMimeFiles.length,
    invalidMimeTypes: [...new Set(invalidMimeFiles.map(file => file.type || 'unknown file type'))],
    invalidSizeCount: files.length - sizedFiles.length,
    tooMany: false,
    validFiles,
  };
}

function getIsAcceptedSize(file: File): boolean {
  return file.size <= MAX_FILESTACK_FILE_SIZE;
}

function getIsAcceptedMimeType(file: File): boolean {
  if (acceptedMimeTypes.has(file.type)) return true;
  return acceptedMimeTypePrefixes.some(prefix => file.type.startsWith(prefix));
}
